import React, { useEffect, useState } from 'react';
import {
    getAiProviders, createAiProvider, updateAiProvider, deleteAiProvider, setPrimaryAiProvider,
    testAiProvider, getProviderModels, getProviderPlans, getAiGuidance, setAiPurposeRouting,
    applyRecommendedRole, getAiProviderGroups
} from '../../services/AiProvidersService';
import AiGuidancePanel, { AiGuidanceAlerts } from './AiGuidancePanel';
import { getAiUsage } from '../../services/SettingsService';
import { AI_MODELS } from '../../utils/aiModels';
import CredentialsGuide from '../../components/CredentialsGuide/CredentialsGuide';
import { AI_GUIDES } from '../../data/setupGuides';
import { confirmDialog } from '../../utils/ConfirmDialog';

function fmtTokens(n) {
    n = Number(n) || 0; // SUM() do Postgres volta bigint como string — coage pra número
    if (!n) return '0';
    if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M';
    if (n >= 1e3) return (n / 1e3).toFixed(0) + 'K';
    return String(n);
}

// Tokens totais (entrada+saída) de uma linha de uso agregado, coagindo as strings bigint
// do SUM() pra número (senão `+` concatena: "301000"+"3000" = "3010003000").
function usageTokens(u) {
    if (!u) return 0;
    return (Number(u.inputTokens) || 0) + (Number(u.outputTokens) || 0);
}

// Marca o teto que veio de MEDIÇÃO, não do que alguém digitou: quando a API recusou por cota, o
// consumo daquele instante virou o teto real e passou a valer sobre o cadastrado. Mostrar isso é o
// que evita a pergunta "por que ele parou em 300K se eu cadastrei 1M?".
function MedidoTag({ row, cadastrado }) {
    const at = row.adaptive && row.adaptive.observedAt;
    const quando = at ? new Date(at).toLocaleDateString('pt-BR') : null;
    return (
        <span className='badge bg-info text-dark border ms-1' style={{ fontSize: 9 }}
            title={`Teto MEDIDO: foi até onde o provedor deixou antes de recusar${quando ? `, em ${quando}` : ''}.`
                + (cadastrado ? ` Vale este número no lugar do cadastrado (${fmtTokens(cadastrado)}) — medição vence palpite.` : '')
                + ' Se uma chamada passar acima dele, a medição é descartada e o teto volta a subir sozinho.'}>
            medido
        </span>
    );
}

const EMPTY_FORM = { id: null, provider: '', apiKey: '', model: '', role: 'primary', priority: 0, isActive: true,
    useForTradeSignal: true, plan: null, dailyTokenLimit: null, weeklyTokenLimit: null, monthlyTokenLimit: null };

// Rótulo curto de recomendação por finalidade (campo bestFor do metadata).
const BEST_FOR_HINT = {
    SIGNAL:  { icon: '⚡', text: 'Recomendado para o sinal de trade (grátis/rápido).' },
    QUALITY: { icon: '🧠', text: 'Recomendado para revisão de estratégia e risco (mais inteligente). Considere deixá-lo fora do sinal de trade para poupar tokens.' },
    BOTH:    { icon: '⚡🧠', text: 'Bom para as duas finalidades: sinal de trade e análise (qualidade + baixo custo).' },
};

/**
 * Gerencia a lista de provedores de IA do usuário. Substitui o bloco
 * single-provider de Settings — permite ter primary + fallbacks que o
 * beholder percorre automaticamente quando o primary falha ou estoura
 * limite.
 *
 * props:
 * - aiProviders: metadata vinda do backend pra popular o dropdown
 *   (mesmo shape que Settings já carregava)
 */
function AiProvidersManager({ aiProviders }) {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(null); // null = fechado; objeto = editando/criando
    const [error, setError] = useState('');
    const [testing, setTesting] = useState(null); // id em teste
    const [reordering, setReordering] = useState(false); // persistindo troca de prioridade via ↑/↓
    const [testResult, setTestResult] = useState(null); // {id, ok, message}
    const [liveModels, setLiveModels] = useState([]); // modelos vivos da API
    const [loadingModels, setLoadingModels] = useState(false);
    const [modelsError, setModelsError] = useState('');
    const [usageByProvider, setUsageByProvider] = useState({});
    const [usageTodayByProvider, setUsageTodayByProvider] = useState({});
    const [plans, setPlans] = useState({});
    const [onlyFree, setOnlyFree] = useState(false); // filtro do dropdown: só provedores com tier grátis
    // Modo do FORMULÁRIO de adicionar/editar: 'simple' (provedor + chave) ou 'advanced' (plano, limites,
    // papel, prioridade, sinal de trade). Preferência só de apresentação → localStorage.
    const [formMode, setFormMode] = useState(() => localStorage.getItem('aiProviderFormMode') || 'simple');
    const changeFormMode = m => { setFormMode(m); try { localStorage.setItem('aiProviderFormMode', m); } catch (_) {} };

    const [guidance, setGuidance] = useState(null);
    const [guidanceBusy, setGuidanceBusy] = useState(false);
    const [suggestionBusy, setSuggestionBusy] = useState(null);   // id do provedor com ação em curso
    const [groupsData, setGroupsData] = useState(null); // fila por finalidade (GET /aiProviders/groups)

    function loadGuidance() {
        getAiGuidance().then(setGuidance).catch(() => {});
    }

    function loadGroups() {
        getAiProviderGroups().then(setGroupsData).catch(() => {});
    }

    function load() {
        setLoading(true);
        getAiProviders()
            .then(rows => { setList(rows || []); setLoading(false); })
            .catch(() => setLoading(false));
        loadGuidance();
        loadGroups();
    }

    async function handleSetPurpose(purpose, providerId) {
        setGuidanceBusy(true);
        try { await setAiPurposeRouting(purpose, providerId); loadGuidance(); }
        catch (_) { setError('Erro ao gravar o papel da IA.'); }
        finally { setGuidanceBusy(false); }
    }

    // Aplica a ação SUGERIDA pela própria linha (hoje só "tirar das automações"). Pede confirmação
    // porque mexe em quem atende o sinal — e a frase da confirmação repete o MOTIVO medido, pra a
    // decisão ser tomada com o número à vista, não pelo susto do botão vermelho.
    async function handleSuggestion(p) {
        if (!p || !p.suggestion) return;
        const meta = aiProviders && aiProviders.find(m => m.name === p.provider);
        const nome = meta ? meta.label : p.provider;
        const ok = await confirmDialog({
            title: `Tirar ${nome} das automações?`,
            message: `Motivo: ${p.suggestion.why}.\n\nEle continua ativo para as outras finalidades e como reserva `
                + `se todos os outros falharem. Dá pra desfazer a qualquer momento na edição do provedor.`,
            confirmLabel: 'Tirar das automações'
        });
        if (!ok) return;
        setSuggestionBusy(p.id);
        try { await updateAiProvider(p.id, { useForTradeSignal: false }); load(); }
        catch (_) { setError('Erro ao tirar o provedor das automações.'); }
        finally { setSuggestionBusy(null); }
    }

    async function handleApplyRole(providerId) {
        setGuidanceBusy(true);
        try { await applyRecommendedRole(providerId); load(); }
        catch (_) { setError('Erro ao aplicar o papel recomendado.'); }
        finally { setGuidanceBusy(false); }
    }

    function loadUsage() {
        getAiUsage()
            .then(data => {
                const monthly = {}, today = {};
                (data.byProvider || []).forEach(r => { monthly[r.provider] = r; });
                (data.todayByProvider || []).forEach(r => { today[r.provider] = r; });
                setUsageByProvider(monthly);
                setUsageTodayByProvider(today);
            })
            .catch(() => {});
    }

    useEffect(() => { load(); loadUsage(); getProviderPlans().then(setPlans).catch(() => {}); }, []);

    function resetModels() {
        setLiveModels([]);
        setModelsError('');
    }

    // Fallbacks na ordem efetiva da cadeia (primary é sempre o 1º e não entra aqui — pra
    // promover já existe o "Tornar primary"). Desempata por id pra ordem estável com
    // prioridades duplicadas.
    function fallbacksOrdered() {
        return list.filter(p => p.role !== 'primary')
            .sort((a, b) => ((a.priority || 0) - (b.priority || 0)) || (a.id - b.id));
    }

    // Move o fallback uma posição pra cima/baixo e persiste a prioridade PELA POSIÇÃO
    // (normaliza 1..n de uma vez — também conserta duplicatas/buracos herdados de edições
    // manuais). Só grava as linhas cuja prioridade mudou.
    async function moveProvider(row, dir) {
        const fbs = fallbacksOrdered();
        const idx = fbs.findIndex(p => p.id === row.id);
        const target = idx + dir;
        if (idx < 0 || target < 0 || target >= fbs.length) return;
        [fbs[idx], fbs[target]] = [fbs[target], fbs[idx]];
        const changes = fbs
            .map((p, i) => ({ id: p.id, priority: i + 1, old: p.priority }))
            .filter(c => c.priority !== c.old);
        setReordering(true);
        try {
            await Promise.all(changes.map(c => updateAiProvider(c.id, { priority: c.priority })));
            load();
        } catch (err) {
            setError('Erro ao reordenar prioridades.');
        } finally {
            setReordering(false);
        }
    }

    function openNew() {
        // Se já tem primary, novo entra como fallback por default
        const hasPrimary = list.some(p => p.role === 'primary');
        setForm({ ...EMPTY_FORM, role: hasPrimary ? 'fallback' : 'primary', priority: list.length });
        setError('');
        resetModels();
    }

    function openEdit(row) {
        setForm({ ...row, apiKey: '' });  // apiKey nunca volta do backend; só preenche se trocar
        setError('');
        resetModels();
    }

    function handleRefreshModels() {
        if (!form.id) return;
        setLoadingModels(true);
        setModelsError('');
        getProviderModels(form.id)
            .then(r => setLiveModels(r.models || []))
            .catch(err => setModelsError(err.response?.data?.error || err.message || 'Falha ao buscar modelos.'))
            .finally(() => setLoadingModels(false));
    }

    function handleSave(e) {
        e.preventDefault();
        setError('');
        if (!form.provider) return setError('Selecione o provedor.');
        if (!form.id && !form.apiKey) return setError('API Key é obrigatória pra novo provedor.');

        const payload = { ...form };
        if (!payload.apiKey) delete payload.apiKey; // não envia em branco no PATCH

        const promise = form.id ? updateAiProvider(form.id, payload) : createAiProvider(payload);
        promise
            .then(() => { setForm(null); load(); })
            .catch(err => setError(err.response?.data?.error || err.message || 'Erro ao salvar.'));
    }

    function handleSetPrimary(id) {
        setPrimaryAiProvider(id).then(load).catch(err => setError(err.message));
    }

    async function handleDelete(id, name) {
        const ok = await confirmDialog({
            title: `Remover ${name}`,
            message: `Tem certeza que quer remover este provedor?\n\nAs automações em uso vão tentar o próximo da cadeia de fallback.`,
            confirmLabel: 'Remover',
            confirmVariant: 'danger'
        });
        if (!ok) return;
        deleteAiProvider(id).then(load).catch(err => setError(err.message));
    }

    function handleToggleActive(row) {
        updateAiProvider(row.id, { isActive: !row.isActive }).then(load);
    }

    function handleTest(row) {
        setTesting(row.id);
        setTestResult(null);
        testAiProvider(row.id)
            .then(r => setTestResult({ id: row.id, ok: true, message: r.message }))
            .catch(err => {
                const msg = err.response?.data?.error || err.message || 'Falha no teste.';
                const detail = err.response?.data?.detail || null;
                setTestResult({ id: row.id, ok: false, message: msg, detail });
            })
            .finally(() => setTesting(null));
    }

    if (loading) return <div className='text-muted py-2' style={{ fontSize: 14 }}>Carregando provedores...</div>;

    // Teto que VALE pra este provedor num período. Com os tetos adaptativos ligados, o backend manda
    // o limite efetivo já resolvido — que pode ser o MEDIDO na recusa real da API (source
    // 'observed'), e esse vence o cadastrado à mão. Sem o opt-in, é o cadastrado mesmo.
    function effectiveLimit(row, period) {
        const a = row.adaptive;
        if (a && a.enabled) {
            const e = period === 'day' ? a.effectiveDaily : a.effectiveMonthly;
            if (e) return e;
        }
        const v = period === 'day' ? row.dailyTokenLimit : row.monthlyTokenLimit;
        return { value: v || null, source: v ? 'configured' : null };
    }

    // Estado operacional de cada provedor. Espelha o que o backend REALMENTE faz — o que não é o
    // mesmo que "estourou o teto":
    //   - teto de conta GRÁTIS (modo estimado) NÃO barra: é palpite nosso sobre a cota de terceiro,
    //     e quem decide que acabou é o provedor. A tela antes dizia "o fallback assume" aqui, e era
    //     mentira nos dois sentidos (o backend nem lia o teto diário).
    //   - quem barra de fato é o teto RÍGIDO (conta paga) e o cooldown (o provedor recusou faz pouco).
    function providerStatus(row) {
        const a = row.adaptive;
        const adaptiveOn = !!(a && a.enabled);
        const dailyUsed = usageTokens(usageTodayByProvider[row.provider]);
        const monthlyUsed = usageTokens(usageByProvider[row.provider]);
        const dailyLimit = effectiveLimit(row, 'day').value;
        const monthlyLimit = effectiveLimit(row, 'month').value;
        const cappedDaily = dailyLimit > 0 && dailyUsed >= dailyLimit;
        const cappedMonthly = monthlyLimit > 0 && monthlyUsed >= monthlyLimit;
        const soft = adaptiveOn && a.mode === 'soft';
        const cooldownUntil = a && a.cooldown && a.cooldown.until > Date.now() ? a.cooldown.until : null;
        // Barra de verdade? Sem o opt-in, só o teto MENSAL barra (é o único que o caminho legado lê).
        const blocks = !!cooldownUntil || (adaptiveOn
            ? (!soft && (cappedDaily || cappedMonthly))
            : cappedMonthly);
        return { cappedDaily, cappedMonthly, capped: cappedDaily || cappedMonthly, adaptiveOn, soft, cooldownUntil, blocks };
    }
    // Provedor que serve agora = 1º ativo que não está BARRADO (não "que não estourou": estourar a
    // estimativa de uma conta grátis não tira ninguém da fila).
    const servingRow = [...list]
        .sort((a, b) => (a.priority || 0) - (b.priority || 0))
        .find(r => r.isActive && !providerStatus(r).blocks);
    const servingId = servingRow ? servingRow.id : null;
    const fmtHora = ts => new Date(ts).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

    return (
        <div className='mb-4'>
            <div className='d-flex justify-content-between align-items-center mb-2'>
                <div>
                    <h5 className='mb-0'>🤖 Provedores de IA</h5>
                    <small className='text-muted'>
                        Configure múltiplos provedores: o <strong>primary</strong> é usado por padrão;
                        os <strong>fallbacks</strong> entram em ação se o primary falhar ou estourar limite.
                    </small>
                </div>
                <button className='btn btn-sm btn-primary' onClick={openNew}>+ Adicionar</button>
            </div>

            {list.length === 0 && (
                <div className='alert alert-info py-2' style={{ fontSize: 13 }}>
                    Nenhum provedor configurado. Adicione pelo menos um pra ativar a análise por IA.
                </div>
            )}

            <AiGuidanceAlerts guidance={guidance} onApplyRole={handleApplyRole} busy={guidanceBusy} />
            {error && !form && <div className='alert alert-danger py-2' style={{ fontSize: 13 }}>{error}</div>}

            {list.length > 0 && (
                <div className='table-responsive'>
                    <table className='table table-sm table-hover align-middle mb-2'>
                        <thead className='thead-light'>
                            <tr>
                                <th>Provedor</th>
                                <th>Modelo</th>
                                <th>Papel</th>
                                <th>Hoje</th>
                                <th>Mês</th>
                                <th className='text-end'>Prioridade</th>
                                <th>Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {list.map(row => {
                                const meta = aiProviders && aiProviders.find(p => p.name === row.provider);
                                const isPrimary = row.role === 'primary';
                                return (
                                    <tr key={row.id} style={!row.isActive ? { opacity: 0.5 } : {}}>
                                        <td><strong title={meta ? meta.note : ''} style={meta && meta.note ? { cursor: 'help' } : {}}>{meta ? meta.label : row.provider}</strong></td>
                                        <td>
                                            <small className='text-muted'>{row.model || 'padrão'}</small>
                                            {/* Natureza do teto. Sem isto, dois provedores com o mesmo número
                                                cadastrado se comportam de formas opostas e a tela não explica. */}
                                            {row.adaptive && row.adaptive.enabled && (
                                                row.adaptive.mode === 'soft'
                                                    ? <span className='badge bg-light text-dark border ms-1' style={{ fontSize: 10 }}
                                                        title='Teto ESTIMADO (conta grátis): o número é palpite nosso sobre a cota do provedor, então não barra — segue usando até ele mesmo recusar, e aí sai da fila por um tempo. Barrar por palpite jogaria fora capacidade grátis.'>
                                                        teto estimado</span>
                                                    : <span className='badge bg-light text-dark border ms-1' style={{ fontSize: 10 }}
                                                        title='Teto RÍGIDO (conta paga): barra ANTES de gastar. Provedor pago não recusa, ele cobra — aqui o teto é a única proteção de custo que existe.'>
                                                        teto rígido</span>
                                            )}
                                            {row.useForTradeSignal === false && (
                                                <span className='badge bg-light text-dark border ms-1' style={{ fontSize: 10 }}
                                                    title='Fora do sinal de trade (sharding) — só revisão de estratégia/risco e fallback'>
                                                    sinal off
                                                </span>
                                            )}
                                            {(() => {
                                                const g = guidance && guidance.providers && guidance.providers.find(p => p.id === row.id);
                                                if (!g || g.monthlySignalCostUsd === null || g.monthlySignalCostUsd === undefined) return null;
                                                return g.monthlySignalCostUsd > 0
                                                    ? <small className='d-block text-warning' style={{ fontSize: 10 }}
                                                        title='Custo mensal ESTIMADO deste provedor continuando no sinal de trade (rateio entre os provedores do sinal, preços públicos do provedor)'>
                                                        ~US${g.monthlySignalCostUsd}/mês no sinal</small>
                                                    : <small className='d-block text-muted' style={{ fontSize: 10 }}>grátis no sinal</small>;
                                            })()}
                                        </td>
                                        <td>
                                            {isPrimary
                                                ? <span className='badge bg-success'>🟢 Primary</span>
                                                : <span className='badge bg-secondary'>Fallback</span>}
                                        </td>
                                        <td style={{ minWidth: 110 }}>{(() => {
                                            const t = usageTodayByProvider[row.provider];
                                            const used = usageTokens(t);
                                            const eff = effectiveLimit(row, 'day');
                                            const limit = eff.value;
                                            if (!used && !limit) return <small className='text-muted'>—</small>;
                                            const pct = limit ? Math.min((used / limit) * 100, 100) : null;
                                            const cls = !pct ? '' : pct >= 90 ? 'bg-danger' : pct >= 70 ? 'bg-warning' : 'bg-success';
                                            return (
                                                <div>
                                                    <small className='text-muted'>{fmtTokens(used)}{limit ? `/${fmtTokens(limit)}` : ''}</small>
                                                    {eff.source === 'observed' && <MedidoTag row={row} cadastrado={row.dailyTokenLimit} />}
                                                    {limit > 0 && <div className='progress mt-1' style={{ height: 4 }}>
                                                        <div className={`progress-bar ${cls}`} style={{ width: `${pct}%` }} />
                                                    </div>}
                                                </div>
                                            );
                                        })()}</td>
                                        <td style={{ minWidth: 110 }}>{(() => {
                                            const used = usageTokens(usageByProvider[row.provider]);
                                            const eff = effectiveLimit(row, 'month');
                                            const limit = eff.value;
                                            if (!used && !limit) return <small className='text-muted'>—</small>;
                                            const pct = limit ? Math.min((used / limit) * 100, 100) : null;
                                            const cls = !pct ? '' : pct >= 90 ? 'bg-danger' : pct >= 70 ? 'bg-warning' : 'bg-success';
                                            return (
                                                <div title={limit ? `Resta ${fmtTokens(Math.max(0, limit - used))} este mês` : ''}>
                                                    <small className='text-muted'>{fmtTokens(used)}{limit ? `/${fmtTokens(limit)}` : ''}</small>
                                                    {eff.source === 'observed' && <MedidoTag row={row} cadastrado={row.monthlyTokenLimit} />}
                                                    {limit > 0 && <div className='progress mt-1' style={{ height: 4 }}>
                                                        <div className={`progress-bar ${cls}`} style={{ width: `${pct}%` }} />
                                                    </div>}
                                                    {limit > 0 && <small className='text-muted d-block' style={{ fontSize: 10 }}>resta {fmtTokens(Math.max(0, limit - used))}</small>}
                                                </div>
                                            );
                                        })()}</td>
                                        <td className='text-end' style={{ whiteSpace: 'nowrap' }}>
                                            {isPrimary ? row.priority : (() => {
                                                const fbs = fallbacksOrdered();
                                                const idx = fbs.findIndex(p => p.id === row.id);
                                                return (
                                                    <>
                                                        <button type='button' className='btn btn-sm btn-outline-secondary py-0 px-1 me-1'
                                                            style={{ fontSize: 10, lineHeight: '14px' }}
                                                            disabled={reordering || idx <= 0}
                                                            title='Subir na ordem de fallback'
                                                            onClick={() => moveProvider(row, -1)}>▲</button>
                                                        <button type='button' className='btn btn-sm btn-outline-secondary py-0 px-1 me-2'
                                                            style={{ fontSize: 10, lineHeight: '14px' }}
                                                            disabled={reordering || idx >= fbs.length - 1}
                                                            title='Descer na ordem de fallback'
                                                            onClick={() => moveProvider(row, 1)}>▼</button>
                                                        {row.priority}
                                                    </>
                                                );
                                            })()}
                                        </td>
                                        <td>
                                            <button
                                                type='button'
                                                className={`btn btn-sm py-0 px-2 ${row.isActive ? 'btn-outline-success' : 'btn-outline-secondary'}`}
                                                onClick={() => handleToggleActive(row)}
                                                title={row.isActive ? 'Clique para desativar' : 'Clique para ativar'}
                                                style={{ fontSize: 11 }}>
                                                {row.isActive ? 'Ativo' : 'Inativo'}
                                            </button>
                                            {row.isActive && (() => {
                                                const st = providerStatus(row);
                                                // Ordem de precedência = a do backend: quem o provedor recusou está fora,
                                                // ponto. Só depois vale discutir teto nosso.
                                                if (st.cooldownUntil) return <span className='badge bg-warning text-dark ms-1'
                                                    title={`O provedor recusou a chamada há pouco. Este está fora da fila até ${fmtHora(st.cooldownUntil)} — insistir antes disso só arrisca bloqueio da conta.`}>
                                                    em espera até {fmtHora(st.cooldownUntil)}</span>;
                                                if (st.capped && st.soft) return <span className='badge bg-light text-dark border ms-1'
                                                    title='Passou da nossa ESTIMATIVA da cota grátis. De propósito não barra: o número é palpite sobre a política do provedor, então segue tentando até ele mesmo recusar.'>
                                                    acima do estimado</span>;
                                                if (st.cappedMonthly) return <span className='badge bg-danger ms-1' title='Estourou o limite mensal de tokens — o fallback assume'>Teto mês</span>;
                                                if (st.cappedDaily && st.adaptiveOn) return <span className='badge bg-warning text-dark ms-1' title='Estourou o limite diário de tokens — o fallback assume'>Teto dia</span>;
                                                if (st.cappedDaily) return <span className='badge bg-light text-dark border ms-1'
                                                    title='Passou do teto diário, mas os tetos adaptativos estão desligados: o backend só barra pelo teto MENSAL. Este provedor continua atendendo.'>
                                                    teto dia (não barra)</span>;
                                                if (row.id === servingId) return <span className='badge bg-success ms-1' title='Provedor que está atendendo as análises agora'>Em uso</span>;
                                                return <span className='badge bg-secondary ms-1' title='Pronto pra assumir se o de cima falhar ou estourar'>Pronto</span>;
                                            })()}
                                        </td>
                                        <td className='text-end'>
                                            <div className='btn-group btn-group-sm' role='group'>
                                                <button type='button' className='btn btn-outline-info py-0 px-2'
                                                    title='Faz uma chamada de teste pra validar a API Key'
                                                    disabled={testing === row.id}
                                                    onClick={() => handleTest(row)} style={{ fontSize: 12 }}>
                                                    <svg width='12' height='12' fill='currentColor' viewBox='0 0 16 16' className='me-1' style={{ verticalAlign: '-2px' }}>
                                                        <path d='M6 .278a.768.768 0 0 1 .08.858 7.208 7.208 0 0 0-.878 3.46c0 4.021 3.278 7.277 7.318 7.277.527 0 1.04-.055 1.533-.16a.787.787 0 0 1 .81.316.733.733 0 0 1-.031.893A8.349 8.349 0 0 1 8.344 16C3.734 16 0 12.286 0 7.71 0 4.266 2.114 1.312 5.124.06A.752.752 0 0 1 6 .278z'/>
                                                    </svg>
                                                    {testing === row.id ? 'testando...' : 'Testar Conexão'}
                                                </button>
                                                {!isPrimary && (
                                                    <button type='button' className='btn btn-outline-success py-0 px-2'
                                                        title='Promover este provedor a primary'
                                                        onClick={() => handleSetPrimary(row.id)} style={{ fontSize: 12 }}>
                                                        <svg width='12' height='12' fill='currentColor' viewBox='0 0 16 16' className='me-1' style={{ verticalAlign: '-2px' }}>
                                                            <path d='M3.612 15.443c-.386.198-.824-.149-.746-.592l.83-4.73L.173 6.765c-.329-.314-.158-.888.283-.95l4.898-.696L7.538.792c.197-.39.73-.39.927 0l2.184 4.327 4.898.696c.441.062.612.636.282.95l-3.522 3.356.83 4.73c.078.443-.36.79-.746.592L8 13.187l-4.389 2.256z'/>
                                                        </svg>
                                                        Tornar primary
                                                    </button>
                                                )}
                                                <button type='button' className='btn btn-outline-primary py-0 px-2'
                                                    title='Editar provedor e API Key'
                                                    onClick={() => openEdit(row)} style={{ fontSize: 12 }}>
                                                    <svg width='12' height='12' fill='currentColor' viewBox='0 0 16 16' className='me-1' style={{ verticalAlign: '-2px' }}>
                                                        <path d='M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z'/>
                                                    </svg>
                                                    Editar
                                                </button>
                                                <button type='button' className='btn btn-outline-danger py-0 px-2'
                                                    title='Remover provedor'
                                                    onClick={() => handleDelete(row.id, meta ? meta.label : row.provider)} style={{ fontSize: 12 }}>
                                                    <svg width='12' height='12' fill='currentColor' viewBox='0 0 16 16' className='me-1' style={{ verticalAlign: '-2px' }}>
                                                        <path d='M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z'/>
                                                        <path d='M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3h11V2h-11v1z'/>
                                                    </svg>
                                                    Remover
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {list.length > 0 && groupsData && groupsData.groups && groupsData.groups.length > 0 && (
                <div className='mt-3'>
                    <h6 className='mb-1'>🧭 Quem faz o quê <small className='text-muted'>(fila por finalidade, do mais recomendado pro reserva)</small></h6>
                    {/* Explica a DINÂMICA: sem isso o usuário vê a fila mudar entre duas visitas e
                        não sabe se é comportamento normal ou defeito. */}
                    {groupsData.dynamics && (
                        <small className='text-muted d-block mb-2' style={{ fontSize: 11 }}>{groupsData.dynamics}</small>
                    )}
                    <div className='row'>
                        {groupsData.groups.map(g => (
                            <div className='col-md-4 mb-2' key={g.group}>
                                <div className='card h-100'>
                                    <div className='card-body py-2 px-3'>
                                        <div className='fw-bold' style={{ fontSize: 13 }}>{g.label}</div>
                                        <small className='text-muted d-block mb-2' style={{ fontSize: 11 }}>{g.hint}</small>
                                        {(!g.providers || g.providers.length === 0) && (
                                            <small className='text-muted'>Nenhum provedor elegível.</small>
                                        )}
                                        {(g.providers || []).map((p, i) => {
                                            const meta = aiProviders && aiProviders.find(m => m.name === p.provider);
                                            const label = meta ? meta.label : p.provider;
                                            const isTop = i === 0;
                                            return (
                                                <div key={p.id}
                                                    className={`ps-2 mb-1 border-start border-3 ${isTop ? 'border-success' : 'border-light'}`}
                                                    style={{ opacity: p.fits === false ? 0.65 : 1 }}>
                                                    <div style={{ fontSize: 12 }}>
                                                        {isTop && <span title='Titular deste grupo hoje'>⭐ </span>}
                                                        <strong>{label}</strong>
                                                        {p.model && <span className='text-muted'> · {p.model}</span>}
                                                        {p.silent && (
                                                            <span className='badge bg-danger ms-1' style={{ fontSize: 9 }}
                                                                title='Não serviu nenhuma chamada nas últimas 24h'>mudo</span>
                                                        )}
                                                        {p.fits === false && (
                                                            <span className='badge bg-secondary ms-1' style={{ fontSize: 9 }}
                                                                title='Não deveria ser titular deste grupo — fica como reserva'>reserva</span>
                                                        )}
                                                    </div>
                                                    <small className='text-muted' style={{ fontSize: 11 }}>{p.reason}</small>
                                                    {/* 3º eixo: DESEMPENHO. Aguenta o trabalho e atende
                                                        quando chamo são outras duas perguntas — esta é a
                                                        única que fala de dinheiro. Com amostra pequena o
                                                        texto diz "ainda medindo", nunca um veredito. */}
                                                    {p.outcome && (
                                                        <small className={`d-block ${p.outcome.measured ? (p.outcome.positive ? 'text-success' : 'text-danger') : 'text-muted'}`}
                                                            style={{ fontSize: 11, opacity: p.outcome.measured ? 1 : 0.7 }}
                                                            title={p.outcome.measured
                                                                ? 'Lucro/prejuízo já realizado das compras que ESTE provedor mandou fazer (pareado por ordem de compra e venda).'
                                                                : `Ainda não há operações fechadas suficientes pra afirmar nada. Mostrar resultado com amostra pequena faria você promover sorte.`}>
                                                            {p.outcome.measured ? (p.outcome.positive ? '↗ ' : '↘ ') : '· '}{p.outcome.text}
                                                        </small>
                                                    )}
                                                    {/* Fecha o laço: em vez de só informar, propõe a ação. */}
                                                    {p.suggestion && (
                                                        <div className='mt-1'>
                                                            <button type='button' className='btn btn-sm btn-outline-danger py-0 px-2'
                                                                style={{ fontSize: 10 }}
                                                                disabled={suggestionBusy === p.id}
                                                                title={`Sugerido porque ${p.suggestion.why}. Reversível: é o mesmo campo "usar no sinal" da edição do provedor.`}
                                                                onClick={() => handleSuggestion(p)}>
                                                                {suggestionBusy === p.id ? 'aplicando...' : p.suggestion.label}
                                                            </button>
                                                            <small className='d-block text-danger' style={{ fontSize: 10 }}>{p.suggestion.why}</small>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {list.length > 0 && (
                <AiGuidancePanel guidance={guidance} list={list} aiProviders={aiProviders}
                    onSetPurpose={handleSetPurpose} busy={guidanceBusy} />
            )}

            {testResult && (
                <div className={`alert ${testResult.ok ? 'alert-success' : 'alert-danger'} py-2 mb-2 d-flex justify-content-between align-items-center`}
                     style={{ fontSize: 13 }}>
                    <span>
                        {testResult.ok ? '✓ ' : '✗ '}{testResult.message}
                        {!testResult.ok && testResult.detail && (
                            <span title={testResult.detail} style={{ cursor: 'help', marginLeft: 6, opacity: 0.6, fontSize: 15 }}>ⓘ</span>
                        )}
                    </span>
                    <button className='btn-close' style={{ fontSize: 10 }} onClick={() => setTestResult(null)} />
                </div>
            )}

            {form && (
                <div className='card border-primary mt-3'>
                    <div className='card-body'>
                        <div className='d-flex justify-content-between align-items-center mb-3'>
                            <h6 className='mb-0'>{form.id ? 'Editar provedor' : 'Novo provedor'}</h6>
                            <div className='btn-group btn-group-sm' role='group' aria-label='Modo do formulário'>
                                <button type='button' className={`btn ${formMode === 'simple' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => changeFormMode('simple')}>Simples</button>
                                <button type='button' className={`btn ${formMode === 'advanced' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => changeFormMode('advanced')}>Avançado</button>
                            </div>
                        </div>
                        {error && <div className='alert alert-danger py-1' style={{ fontSize: 13 }}>{error}</div>}
                        <div className='row'>
                            <div className='col-md-6 mb-2'>
                                <div className='d-flex justify-content-between align-items-center'>
                                    <label className='form-label mb-0'>Provedor</label>
                                    {!form.id && (
                                        <div className='form-check form-switch' style={{ fontSize: 12 }}>
                                            <input className='form-check-input' type='checkbox' id='onlyFreeProviders'
                                                checked={onlyFree} onChange={e => setOnlyFree(e.target.checked)} />
                                            <label className='form-check-label text-muted' htmlFor='onlyFreeProviders'>só gratuitos</label>
                                        </div>
                                    )}
                                </div>
                                <select className='form-select form-select-sm' value={form.provider}
                                    onChange={e => {
                                        const name = e.target.value;
                                        const meta = (aiProviders || []).find(p => p.name === name);
                                        // Nudge: premium (bestFor QUALITY) entra FORA do sinal de trade por padrão
                                        // (reservado a eval/risco). Free/both entram no pool. O usuário pode mudar abaixo.
                                        const signalDefault = !meta || meta.bestFor !== 'QUALITY';
                                        setForm({ ...form, provider: name, model: '', useForTradeSignal: signalDefault });
                                        resetModels();
                                    }}
                                    disabled={!!form.id}>
                                    <option value=''>Selecionar...</option>
                                    {(aiProviders || []).filter(p => !onlyFree || p.tier === 'FREE_TIER').map(p => {
                                        const prefix = p.tier === 'FREE_TIER' ? '🟢' : '💲';
                                        const star = p.recommended ? ' ★' : '';
                                        // Quantas contas DESTE provedor o usuário já tem (múltiplas são
                                        // permitidas = mais capacidade no sharding; só avisamos, não bloqueamos).
                                        const already = list.filter(r => r.provider === p.name).length;
                                        const dup = already > 0 ? ` · já cadastrado${already > 1 ? ` (${already})` : ''}` : '';
                                        return (
                                            <option key={p.name} value={p.name}>
                                                {prefix} {p.label}{star}{dup}
                                            </option>
                                        );
                                    })}
                                </select>
                                {(() => {
                                    const meta = (aiProviders || []).find(p => p.name === form.provider);
                                    if (!meta) return null;
                                    const hint = BEST_FOR_HINT[meta.bestFor];
                                    const already = !form.id ? list.filter(r => r.provider === form.provider).length : 0;
                                    return (
                                        <>
                                            {meta.note && <small className='text-muted d-block mt-1'>ℹ️ {meta.note}</small>}
                                            {hint && <small className='text-info d-block'>{hint.icon} {hint.text}</small>}
                                            {already > 0 && (
                                                <small className='text-warning d-block'>
                                                    ⚠️ Você já tem {already} conta{already > 1 ? 's' : ''} deste provedor. Adicionar outra
                                                    (com chave diferente) é permitido e <strong>multiplica a cota grátis</strong> no modo Distribuído.
                                                </small>
                                            )}
                                        </>
                                    );
                                })()}
                                {form.id && <small className='text-muted d-block'>Provedor não pode ser trocado depois de criado — exclua e recadastre.</small>}
                            </div>
                            <div className='col-md-6 mb-2'>
                                <div className='d-flex justify-content-between align-items-center'>
                                    <label className='form-label mb-0'>Modelo</label>
                                    {form.id && (
                                        <button type='button' className='btn btn-link btn-sm p-0'
                                            style={{ fontSize: 12 }} disabled={loadingModels}
                                            title='Busca a lista de modelos direto da API do provedor'
                                            onClick={handleRefreshModels}>
                                            {loadingModels ? 'buscando...' : '↻ Atualizar da API'}
                                        </button>
                                    )}
                                </div>
                                {(() => {
                                    const curated = AI_MODELS[form.provider] || [];
                                    const curatedValues = curated.map(m => m.value);
                                    const others = liveModels.filter(id => !curatedValues.includes(id));
                                    return (
                                        <select className='form-select form-select-sm' value={form.model || ''}
                                            onChange={e => setForm({ ...form, model: e.target.value })}>
                                            <option value=''>Padrão do provedor</option>
                                            <optgroup label='Recomendados'>
                                                {curated.map(m => (
                                                    <option key={m.value} value={m.value}>{m.label}</option>
                                                ))}
                                            </optgroup>
                                            {others.length > 0 && (
                                                <optgroup label='Outros disponíveis (API)'>
                                                    {others.map(id => (
                                                        <option key={id} value={id}>{id}</option>
                                                    ))}
                                                </optgroup>
                                            )}
                                        </select>
                                    );
                                })()}
                                {modelsError && <small className='text-danger d-block'>{modelsError}</small>}
                                {form.model && liveModels.length > 0 && !liveModels.includes(form.model) && (
                                    <small className='text-warning d-block'>
                                        ⚠ Este modelo não aparece na API — pode ter sido descontinuado pelo provedor.
                                    </small>
                                )}
                            </div>
                        </div>
                        {form.provider && AI_GUIDES[form.provider] && (
                            <div className='row'>
                                <div className='col-12 mb-2'>
                                    <CredentialsGuide guide={AI_GUIDES[form.provider]} />
                                </div>
                            </div>
                        )}

                        <div className='row'>
                            <div className='col-12 mb-2'>
                                <label className='form-label'>API Key {form.id ? '(deixe em branco pra manter)' : '(nova)'}</label>
                                <input type='password' className='form-control form-control-sm' value={form.apiKey || ''}
                                    placeholder={form.id ? '••••••••' : 'Cole a API Key aqui'}
                                    onChange={e => setForm({ ...form, apiKey: e.target.value })} />
                                <small className='text-muted'>Armazenada de forma criptografada (AES).</small>
                            </div>
                        </div>

                        {form.id && usageByProvider[form.provider] && (() => {
                            const u = usageByProvider[form.provider];
                            const total = usageTokens(u);
                            return (
                                <div className='alert alert-secondary py-2 mb-2' style={{ fontSize: 12 }}>
                                    📊 Consumo este mês: <strong>{fmtTokens(total)} tokens</strong>
                                    {' '}({fmtTokens(u.inputTokens)} entrada + {fmtTokens(u.outputTokens)} saída)
                                    {' · '}{u.calls} chamadas
                                </div>
                            );
                        })()}
                        {formMode === 'advanced' && (<>
                        {form.provider && plans[form.provider] && (
                            <div className='row'>
                                <div className='col-md-6 mb-2'>
                                    <label className='form-label'>Plano <small className='text-muted'>(preenche limites automaticamente)</small></label>
                                    <select className='form-select form-select-sm' value={form.plan || ''}
                                        onChange={e => {
                                            const pid = e.target.value || null;
                                            const p = pid && plans[form.provider]?.find(x => x.id === pid);
                                            setForm({ ...form, plan: pid,
                                                dailyTokenLimit:   p ? (p.dailyLimit   || null) : form.dailyTokenLimit,
                                                weeklyTokenLimit:  p ? (p.weeklyLimit  || null) : form.weeklyTokenLimit,
                                                monthlyTokenLimit: p ? (p.monthlyLimit || null) : form.monthlyTokenLimit,
                                            });
                                        }}>
                                        <option value=''>Não informado</option>
                                        {plans[form.provider].map(p => (
                                            <option key={p.id} value={p.id}>{p.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        )}
                        <div className='row'>
                            <div className='col-md-4 mb-2'>
                                <label className='form-label'>Limite diário <small className='text-muted'>(tokens)</small></label>
                                <input type='number' className='form-control form-control-sm' value={form.dailyTokenLimit || ''}
                                    min='0' placeholder='Sem limite'
                                    onChange={e => setForm({ ...form, dailyTokenLimit: e.target.value ? parseInt(e.target.value) : null })} />
                            </div>
                            <div className='col-md-4 mb-2'>
                                <label className='form-label'>Limite semanal <small className='text-muted'>(tokens)</small></label>
                                <input type='number' className='form-control form-control-sm' value={form.weeklyTokenLimit || ''}
                                    min='0' placeholder='Sem limite'
                                    onChange={e => setForm({ ...form, weeklyTokenLimit: e.target.value ? parseInt(e.target.value) : null })} />
                            </div>
                            <div className='col-md-4 mb-2'>
                                <label className='form-label'>Limite mensal <small className='text-muted'>(tokens)</small></label>
                                <input type='number' className='form-control form-control-sm' value={form.monthlyTokenLimit || ''}
                                    min='0' placeholder='Sem limite'
                                    onChange={e => setForm({ ...form, monthlyTokenLimit: e.target.value ? parseInt(e.target.value) : null })} />
                            </div>
                            <div className='col-12'>
                                <small className='text-muted'>Quando atingido, o beholder pula para o próximo fallback.</small>
                            </div>
                        </div>

                        <div className='row'>
                            <div className='col-md-6 mb-2'>
                                <label className='form-label'>Papel</label>
                                <select className='form-select form-select-sm' value={form.role}
                                    onChange={e => setForm({ ...form, role: e.target.value })}>
                                    <option value='primary'>Primary (usado por padrão)</option>
                                    <option value='fallback'>Fallback (entra quando primary falha)</option>
                                </select>
                                {form.role === 'primary' && <small className='text-warning'>Vai rebaixar qualquer outro primary atual.</small>}
                            </div>
                            <div className='col-md-6 mb-2'>
                                <label className='form-label'>Prioridade (entre fallbacks)</label>
                                <input type='number' className='form-control form-control-sm' value={form.priority}
                                    min='0' max='99' onChange={e => setForm({ ...form, priority: parseInt(e.target.value) || 0 })} />
                                <small className='text-muted'>Menor = tentado primeiro.</small>
                            </div>
                        </div>
                        <div className='row'>
                            <div className='col-12 mb-2'>
                                <div className='form-check form-switch'>
                                    <input className='form-check-input' type='checkbox' id='useForTradeSignal'
                                        checked={form.useForTradeSignal !== false}
                                        onChange={e => setForm({ ...form, useForTradeSignal: e.target.checked })} />
                                    <label className='form-check-label' htmlFor='useForTradeSignal'>
                                        Usar no <strong>sinal de trade</strong> (modo Distribuído/sharding)
                                    </label>
                                </div>
                                <small className='text-muted'>
                                    Desligue para manter este provedor FORA do sinal por-ativo de alta frequência —
                                    útil para reservar provedores premium só à revisão de estratégia/risco e poupar tokens.
                                    Ele continua como fallback se os demais falharem.
                                </small>
                            </div>
                        </div>
                        </>)}
                        <div className='d-flex justify-content-end gap-2 mt-2'>
                            <button type='button' className='btn btn-sm btn-secondary' onClick={() => setForm(null)}>Cancelar</button>
                            {form.id && (
                                <button type='button' className='btn btn-sm btn-outline-info'
                                    disabled={testing === form.id}
                                    onClick={() => handleTest({ id: form.id })}>
                                    {testing === form.id ? 'testando...' : '🔌 Testar Conexão'}
                                </button>
                            )}
                            <button type='button' className='btn btn-sm btn-primary' onClick={handleSave}>Salvar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AiProvidersManager;
